import { useCallback, useEffect, useMemo, useState } from 'react'

import { customEvent, EventType } from '@core/customEvent'
import { isFalsy } from '@core/helpers/boolean'
import { isIframe } from '@core/window'

import { PIP_IFRAME_NAME } from './constants'
import { hasPIPIframe } from './hasPIPIframe'
import type { PIPParams, PIPTarget } from './types'
import { usePIPControls } from './usePIPControls'

export const TargetEnum = {
  TOP: '_top',
  PIP_IFRAME: PIP_IFRAME_NAME,
} as const

export type TargetEnum = (typeof TargetEnum)[keyof typeof TargetEnum]

type Target = TargetEnum | PIPTarget

interface Props {
  url: string
  params?: PIPParams
  pipTargetOverride?: PIPTarget
}

interface Return {
  url: string
  target: Target
  origin: string
  isOriginMatch: boolean
  onClick: (event?: React.MouseEvent) => void
}

/**
 * Hook providing the url, target and onClick handler for any links that
 * can potentially trigger Picture-in-Picture (PIP) mode. E.g. CTA, links
 * in chat messages...
 * @param IProps
 */
export function usePIPTarget(props: Props): Return {
  const { params, pipTargetOverride } = props
  const { enter: enterPIP } = usePIPControls({
    enabled: params?.pip && params.pip_navigate,
    params: params || {},
  })

  const [url, origin, isOriginMatch] = useMemo(() => {
    const url =
      props.url && props.url.startsWith('/') && !props.url.startsWith('//')
        ? window.location.origin + props.url
        : props.url

    let origin = 'unknown'
    try {
      origin = url ? new URL(url).origin : ''
    } catch (err) {}

    const isOriginMatch =
      typeof window !== 'undefined' &&
      (origin === window.location.origin || origin.endsWith('.myshopify.com'))

    return [url, origin, isOriginMatch]
  }, [props.url])

  const [target, setTarget] = useState<Target>(
    isOriginMatch ? '_self' : '_blank',
  )

  useEffect(() => {
    const effectivePipTarget = pipTargetOverride ?? params?.pip_target

    if (
      effectivePipTarget &&
      ['_self', '_blank'].includes(effectivePipTarget)
    ) {
      setTarget(effectivePipTarget)
      return
    }

    if (isOriginMatch) {
      if (hasPIPIframe()) {
        setTarget(TargetEnum.PIP_IFRAME)
      } else if (isIframe()) {
        setTarget(TargetEnum.TOP)
      } else {
        setTarget('_self')
      }
    } else {
      setTarget('_blank')
    }
  }, [isOriginMatch, params?.pip_target, pipTargetOverride])

  const onClick = useCallback(
    (event?: React.MouseEvent) => {
      if (isOriginMatch) {
        customEvent({
          type: EventType.FW_NAVIGATE,
          data: {
            url,
          },
        })

        if (isFalsy(params?.pip) || params?.pip_navigate === false) {
          if (params?.pip_navigate === false) {
            event?.preventDefault()
            event?.stopPropagation()
          }

          return
        }

        if (isFalsy(pipTargetOverride) && isFalsy(params?.pip_target)) {
          event?.preventDefault()
          event?.stopPropagation()

          enterPIP(url)
        }
      }
    },
    [isOriginMatch, params, url, enterPIP, pipTargetOverride],
  )

  return { url, origin, isOriginMatch, target, onClick }
}
